import { useCallback } from 'react';
import { createDefaultCameraZoomSetting, type CameraZoomSetting } from '@sorrem/shared';
import { createValueStore } from '@sorrem-client/ui/shared/value-store';
import { useAccountSetting } from './use-account-setting';
import { useValueStoreSelector } from './value-store';

const DEFAULT_CAMERA_ZOOM: Readonly<CameraZoomSetting> = createDefaultCameraZoomSetting();

/** The zoom level the settings slider is being dragged through, before it is saved; null when it isn't. */
const cameraZoomPreview = createValueStore<number | null>(null);

interface CameraZoomState {
    /** The zoom to show: a level being dragged through, else the account's; 1 and unlocked until settings load. */
    cameraZoom: Readonly<CameraZoomSetting>;
    /** Shows a level at once without saving it: the slider being dragged. */
    previewLevel: (level: number) => void;
    /** Saves a level to the account (applied optimistically) and ends any preview. */
    setLevel: (level: number) => void;
    /** Saves whether the mouse wheel may change the zoom. */
    setLocked: (locked: boolean) => void;
}

/**
 * The account's camera zoom for runs, shown in the settings pop-over. The
 * mouse wheel moves it too, unless it is locked (see `useSyncCameraZoom`).
 */
export function useCameraZoom(): CameraZoomState {
    const { value: saved, set: setCameraZoom } = useAccountSetting('cameraZoom', DEFAULT_CAMERA_ZOOM);
    const preview = useValueStoreSelector(cameraZoomPreview, (level) => level);

    const previewLevel = useCallback((level: number): void => {
        cameraZoomPreview.set(level);
    }, []);

    const setLevel = useCallback((level: number): void => {
        setCameraZoom((current) => (current.level === level ? current : { ...current, level }));
        cameraZoomPreview.set(null);
    }, [setCameraZoom]);

    const setLocked = useCallback((locked: boolean): void => {
        setCameraZoom((current) => (current.isLocked === locked ? current : { ...current, isLocked: locked }));
    }, [setCameraZoom]);

    const cameraZoom = preview === null || preview === saved.level ? saved : { ...saved, level: preview };
    return { cameraZoom, previewLevel, setLevel, setLocked };
}
